import React, { useState, useCallback } from 'react';
import { useNavigateWithNewTab } from '../hooks/useNavigateWithNewTab';
import {
  Button,
  Input,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  DatePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import {
  ExternalLink,
  Plus,
  X,
  AlertTriangle,
  ChevronsUpDown,
  Home,
  Download,
  ChevronDown,
} from 'lucide-react';
import type {
  NoaResponse,
  NoaValidationError,
} from '../services/api/types.gen';
import {
  updateNoaNoasNoaIdPatch,
  setPrimaryDiagnosisNoasNoaIdPrimaryDiagnosisPatch,
  validateNoaNoasNoaIdValidatePost,
  submitNoa837iNoasNoaIdSubmitPost,
  sendNoaNoasNoaIdSendPost,
  markNotApplicableNoasNoaIdMarkNotApplicablePost,
  revertToNotReadyNoasNoaIdRevertToNotReadyPost,
  searchDiagnosisCodesRouteDiagnosisCodesSearchGet,
  getNoa837iNoasNoaId837iGet,
  getNoaUb04PdfNoasNoaIdUb04Get,
} from '../services/api/sdk.gen';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { client } from '../services/api';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import { DateField } from './FormFields';
import { CONDITION_CODES } from '../data/conditionCodes';
import { OCCURRENCE_CODES } from '../data/occurrenceCodes';

interface NOAFormProps {
  noa: NoaResponse;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: NoaResponse) => void;
}

const ReadonlyField: React.FC<{
  label: string;
  value: string | null | undefined;
  href?: string;
}> = ({ label, value, href }) => {
  const navigateTo = useNavigateWithNewTab();
  return (
    <div className='flex flex-col gap-0.5'>
      <span className='text-xs text-muted-foreground'>{label}</span>
      <div className='flex items-center gap-1'>
        <span className='text-sm border-b border-muted text-foreground'>
          {value || '—'}
        </span>
        {href && (
          <button
            onClick={navigateTo(href)}
            className='text-muted-foreground hover:text-primary'
          >
            <ExternalLink className='h-3 w-3' />
          </button>
        )}
      </div>
    </div>
  );
};

const ErrorsPanel: React.FC<{ errors: NoaValidationError[] }> = ({
  errors,
}) => {
  if (errors.length === 0) {
    return (
      <div className='rounded-md bg-green-50 border border-green-200 p-3'>
        <p className='text-sm text-green-700 font-medium'>
          No validation errors
        </p>
        <p className='text-xs text-green-600'>This NOA is ready to send.</p>
      </div>
    );
  }
  return (
    <div className='rounded-md bg-red-50 border border-red-200 p-3 space-y-1'>
      <p className='text-sm font-medium text-red-700 flex items-center gap-1'>
        <AlertTriangle className='h-4 w-4' />
        Validation Errors
      </p>
      {errors.map((e, i) => (
        <p key={i} className='text-xs text-red-600'>
          • {e.message}
        </p>
      ))}
    </div>
  );
};

const SORTED_CONDITION_CODES = [...CONDITION_CODES].sort((a, b) => {
  if (a.homeHealth !== b.homeHealth) return a.homeHealth ? -1 : 1;
  return a.code.localeCompare(b.code);
});

const SORTED_OCCURRENCE_CODES = [...OCCURRENCE_CODES].sort((a, b) => {
  if (a.homeHealth !== b.homeHealth) return a.homeHealth ? -1 : 1;
  return a.code.localeCompare(b.code);
});

function ConditionCodeCombobox({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = CONDITION_CODES.find(c => c.code === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className='flex-1 flex items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-xs text-left hover:bg-accent transition-colors min-w-0'>
          <span
            className={`truncate ${selected ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            {selected
              ? `${selected.code} — ${selected.description}`
              : 'Select code…'}
          </span>
          <ChevronsUpDown className='h-3 w-3 text-muted-foreground shrink-0 ml-1' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-96 p-0' align='start'>
        <Command>
          <CommandInput
            placeholder='Search code or description…'
            className='text-xs'
          />
          <CommandList className='max-h-60'>
            <CommandEmpty className='text-xs py-2 text-center text-muted-foreground'>
              No codes found.
            </CommandEmpty>
            <CommandGroup>
              {SORTED_CONDITION_CODES.map(c => (
                <CommandItem
                  key={c.code}
                  value={`${c.code} ${c.description}`}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                  className='text-xs'
                >
                  <span className='font-mono font-semibold w-6 shrink-0'>
                    {c.code}
                  </span>
                  <span className='flex-1 ml-2 truncate'>{c.description}</span>
                  {c.homeHealth && (
                    <span title='Common in home health'>
                      <Home className='h-3 w-3 ml-1 shrink-0 text-teal-600' />
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function OccurrenceCodeCombobox({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = OCCURRENCE_CODES.find(c => c.code === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className='w-28 flex items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-xs text-left hover:bg-accent transition-colors shrink-0'>
          <span
            className={`truncate ${selected ? 'text-foreground font-mono' : 'text-muted-foreground'}`}
          >
            {selected ? selected.code : 'Code…'}
          </span>
          <ChevronsUpDown className='h-3 w-3 text-muted-foreground shrink-0 ml-1' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-96 p-0' align='start'>
        <Command>
          <CommandInput
            placeholder='Search code or description…'
            className='text-xs'
          />
          <CommandList className='max-h-60'>
            <CommandEmpty className='text-xs py-2 text-center text-muted-foreground'>
              No codes found.
            </CommandEmpty>
            <CommandGroup>
              {SORTED_OCCURRENCE_CODES.map(c => (
                <CommandItem
                  key={c.code}
                  value={`${c.code} ${c.description}`}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                  className='text-xs'
                >
                  <span className='font-mono font-semibold w-6 shrink-0'>
                    {c.code}
                  </span>
                  <span className='flex-1 ml-2 truncate'>{c.description}</span>
                  {c.homeHealth && (
                    <span title='Common in home health'>
                      <Home className='h-3 w-3 ml-1 shrink-0 text-teal-600' />
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const NOAForm: React.FC<NOAFormProps> = ({ noa, open, onClose, onSaved }) => {
  const navigateTo = useNavigateWithNewTab();
  const { user } = useStore(useShallow(state => ({ user: state.user })));
  const isAdmin = user?.role === 'admin';
  const [conditionCodes, setConditionCodes] = useState<string[]>(
    noa.condition_codes.length ? noa.condition_codes : ['']
  );
  const [occurrenceCodes, setOccurrenceCodes] = useState<
    { code: string; date: string }[]
  >(
    noa.occurrence_codes.length
      ? noa.occurrence_codes
      : [{ code: '', date: '' }]
  );
  const [physicianNpi, setPhysicianNpi] = useState(noa.physician_npi || '');
  const [remarks, setRemarks] = useState(noa.remarks || '');
  const [dcnIcnNumber, setDcnIcnNumber] = useState(noa.dcn_icn_number || '');
  const [claimNumber, setClaimNumber] = useState(noa.claim_number || '');
  const [sentDate, setSentDate] = useState(noa.sent_date || '');
  const [kxModifier, setKxModifier] = useState<boolean | null>(
    noa.kx_modifier ?? null
  );
  const [kxModifierRemarks, setKxModifierRemarks] = useState(
    noa.kx_modifier_remarks || ''
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Primary diagnosis inline search
  const [dxSearchOpen, setDxSearchOpen] = useState(false);
  const [dxSearchQuery, setDxSearchQuery] = useState('');
  const [dxSearchResults, setDxSearchResults] = useState<
    { name: string; desc: string }[]
  >([]);
  const [localPrimaryDx, setLocalPrimaryDx] = useState<{
    code: string;
    description: string;
  } | null>(
    noa.primary_dx_code
      ? {
          code: noa.primary_dx_code,
          description: noa.primary_dx_description || '',
        }
      : null
  );

  const handleDxSearch = useCallback(async (query: string) => {
    setDxSearchQuery(query);
    if (query.length < 2) {
      setDxSearchResults([]);
      return;
    }
    const resp = await searchDiagnosisCodesRouteDiagnosisCodesSearchGet({
      client,
      query: { search_text: query, limit: 10 },
    });
    if (!resp.error && Array.isArray(resp.data)) {
      setDxSearchResults(resp.data);
    }
  }, []);

  const handleDxSelect = async (code: string, desc: string) => {
    setDxSearchOpen(false);
    setDxSearchQuery('');
    setDxSearchResults([]);
    setError(null);
    try {
      const resp = await setPrimaryDiagnosisNoasNoaIdPrimaryDiagnosisPatch({
        client,
        path: { noa_id: noa.id },
        body: { icd_code: code },
      });
      if (resp.error) throw new Error('Failed to set diagnosis code');
      const updated = resp.data;
      setLocalPrimaryDx({ code, description: desc });
      setLocalValidationErrors(updated.validation_errors);
      setLocalStatus(updated.status);
      onSaved(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to set diagnosis code');
    }
  };
  const [localValidationErrors, setLocalValidationErrors] = useState<
    NoaValidationError[]
  >(noa.validation_errors);
  const [localStatus, setLocalStatus] = useState(noa.status);

  const handleExport = async (format: 'edi' | 'json') => {
    const resp = await getNoa837iNoasNoaId837iGet({
      client,
      path: { noa_id: noa.id },
      query: { format },
      responseType: 'blob',
    });
    if (resp.error) {
      setError('Failed to download 837I file');
      return;
    }
    const blob =
      resp.data instanceof Blob ? resp.data : new Blob([resp.data as BlobPart]);
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `noa_${noa.id}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  };

  const handleExportPdf = async () => {
    const resp = await getNoaUb04PdfNoasNoaIdUb04Get({
      client,
      path: { noa_id: noa.id },
      // UB-04 export returns application/pdf, so this caller intentionally
      // treats the generated response as a Blob.
      responseType: 'blob',
    });
    if (resp.error) {
      setError('Failed to download UB-04 PDF');
      return;
    }
    const blob =
      resp.data instanceof Blob ? resp.data : new Blob([resp.data as BlobPart]);
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  };

  const canExportUb04 =
    localStatus === 'ready_to_send' ||
    localStatus === 'sent' ||
    localStatus === 'paid';
  const canExport837i =
    localStatus === 'ready_to_send' || localStatus === 'sent';

  const handleForceMarkSent = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const resp = await sendNoaNoasNoaIdSendPost({
        client,
        path: { noa_id: noa.id },
      });
      if (resp.error) throw new Error('Failed to mark NOA as sent');
      const updated = resp.data;
      setLocalStatus(updated.status);
      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkNotApplicable = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const resp = await markNotApplicableNoasNoaIdMarkNotApplicablePost({
        client,
        path: { noa_id: noa.id },
      });
      if (resp.error) throw new Error('Failed to mark as not applicable');
      const updated = resp.data;
      setLocalStatus(updated.status);
      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevertToNotReady = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const resp = await revertToNotReadyNoasNoaIdRevertToNotReadyPost({
        client,
        path: { noa_id: noa.id },
      });
      if (resp.error) throw new Error('Failed to revert status');
      const updated = resp.data;
      setLocalStatus(updated.status);
      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const patientDob = noa.patient_dob
    ? formatDateToMMDDYYYY(parseISOToLocalDate(noa.patient_dob))
    : null;
  const socDate = noa.soc_date
    ? formatDateToMMDDYYYY(parseISOToLocalDate(noa.soc_date))
    : null;

  const buildPayload = () => ({
    condition_codes: conditionCodes.filter(c => c.trim()),
    occurrence_codes: occurrenceCodes.filter(o => o.code.trim()),
    physician_npi: physicianNpi || null,
    remarks: remarks || null,
    dcn_icn_number: dcnIcnNumber || null,
    claim_number: claimNumber || null,
    sent_date: sentDate || null,
    kx_modifier: kxModifier,
    kx_modifier_remarks: kxModifierRemarks || null,
  });

  const doSave = async (): Promise<NoaResponse | null> => {
    const payload = buildPayload();
    const patchResp = await updateNoaNoasNoaIdPatch({
      client,
      path: { noa_id: noa.id },
      body: payload,
    });
    if (patchResp.error) {
      throw new Error('Failed to save NOA');
    }
    const validateResp = await validateNoaNoasNoaIdValidatePost({
      client,
      path: { noa_id: noa.id },
    });
    if (validateResp.error) {
      throw new Error('Failed to validate NOA');
    }
    return validateResp.data;
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const updated = await doSave();
      if (updated) {
        setLocalValidationErrors(updated.validation_errors);
        setLocalStatus(updated.status);
        onSaved(updated);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const updated = await doSave();
      if (!updated) return;
      setLocalValidationErrors(updated.validation_errors);
      setLocalStatus(updated.status);

      if (updated.status !== 'ready_to_send') {
        setError(
          'NOA has validation errors and cannot be sent. Please fix them first.'
        );
        onSaved(updated);
        return;
      }

      const sendResp = await submitNoa837iNoasNoaIdSubmitPost({
        client,
        path: { noa_id: noa.id },
      });
      if (sendResp.error) {
        throw new Error('Failed to submit NOA to Medicare');
      }
      const submitData = sendResp.data;
      onSaved(submitData.noa);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) onClose();
      }}
    >
      <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            Notice of Admission — {noa.patient_first_name}{' '}
            {noa.patient_last_name}
          </DialogTitle>
        </DialogHeader>

        {/* Validation errors — full width, below header */}
        <div className='mt-4'>
          <ErrorsPanel errors={localValidationErrors} />
        </div>

        <div className='mt-4 space-y-4'>
          {/* Patient / Admission (readonly) */}
          <div className='rounded-md border p-3 space-y-3'>
            <div className='flex items-center gap-2'>
              <p className='text-xs font-semibold uppercase text-muted-foreground'>
                Patient &amp; Admission Info
              </p>
              <button
                onClick={navigateTo(noa.patient_tab_url)}
                className='text-muted-foreground hover:text-primary'
              >
                <ExternalLink className='h-3 w-3' />
              </button>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <ReadonlyField
                label='Patient Name'
                value={`${noa.patient_first_name} ${noa.patient_last_name}`}
              />
              <ReadonlyField label='MRN' value={noa.patient_mrn} />
              <ReadonlyField label='Date of Birth' value={patientDob} />
              <ReadonlyField
                label='Subscriber ID (MBI)'
                value={noa.subscriber_id}
              />
              <ReadonlyField
                label='Address'
                value={
                  [
                    noa.patient_address,
                    noa.patient_city,
                    noa.patient_state,
                    noa.patient_zip,
                  ]
                    .filter(Boolean)
                    .join(', ') || null
                }
              />
              <ReadonlyField label='Start of Care' value={socDate} />
              {localPrimaryDx ? (
                <ReadonlyField
                  label='Primary Diagnosis'
                  value={`${localPrimaryDx.code}${localPrimaryDx.description ? ' — ' + localPrimaryDx.description : ''}`}
                />
              ) : (
                <div className='flex flex-col gap-0.5'>
                  <span className='text-xs text-muted-foreground'>
                    Primary Diagnosis
                  </span>
                  <Popover open={dxSearchOpen} onOpenChange={setDxSearchOpen}>
                    <PopoverTrigger asChild>
                      <button className='flex items-center gap-1 text-sm border-b border-dashed border-muted-foreground text-muted-foreground hover:text-primary w-fit'>
                        <Plus className='h-3 w-3' />
                        Set diagnosis code
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className='p-0 w-80' align='start'>
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder='Search ICD-10 codes…'
                          value={dxSearchQuery}
                          onValueChange={handleDxSearch}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {dxSearchQuery.length < 2
                              ? 'Type to search…'
                              : 'No results found.'}
                          </CommandEmpty>
                          <CommandGroup>
                            {dxSearchResults.map(r => (
                              <CommandItem
                                key={r.name}
                                value={r.name}
                                onSelect={() => handleDxSelect(r.name, r.desc)}
                              >
                                <span className='font-mono mr-2'>{r.name}</span>
                                <span className='text-muted-foreground text-xs'>
                                  {r.desc}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              <ReadonlyField label='Payer' value={noa.payer_name} />
              <ReadonlyField label='Agency' value={noa.agency_name} />
              <ReadonlyField label='Branch' value={noa.branch_name} />
              <ReadonlyField label='Type of Bill' value='32A' />
              <ReadonlyField
                label='Status'
                value={localStatus
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, c => c.toUpperCase())}
              />
            </div>
          </div>

          {/* Editable fields */}
          <div className='rounded-md border p-3 space-y-3'>
            <p className='text-xs font-semibold uppercase text-muted-foreground'>
              Editable Fields
            </p>

            {/* Condition codes */}
            <div className='space-y-1'>
              <div className='flex items-center gap-1'>
                <label className='text-sm font-medium'>Condition Codes</label>
                <Home className='h-3 w-3 text-teal-600' />
                <span className='text-xs text-muted-foreground'>
                  = used in home health
                </span>
              </div>
              {conditionCodes.map((code, i) => (
                <div key={i} className='flex items-center gap-2'>
                  <ConditionCodeCombobox
                    value={code}
                    onChange={v => {
                      const next = [...conditionCodes];
                      next[i] = v;
                      setConditionCodes(next);
                    }}
                  />
                  <button
                    onClick={() =>
                      setConditionCodes(
                        conditionCodes.filter((_, j) => j !== i)
                      )
                    }
                    className='text-muted-foreground hover:text-destructive shrink-0'
                  >
                    <X className='h-4 w-4' />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setConditionCodes([...conditionCodes, ''])}
                className='flex items-center gap-1 text-xs text-primary hover:underline'
              >
                <Plus className='h-3 w-3' /> Add Condition Code
              </button>
            </div>

            {/* Occurrence codes */}
            <div className='space-y-1'>
              <div className='flex items-center gap-1'>
                <label className='text-sm font-medium'>Occurrence Codes</label>
                <Home className='h-3 w-3 text-teal-600' />
                <span className='text-xs text-muted-foreground'>
                  = used in home health
                </span>
              </div>
              {occurrenceCodes.map((occ, i) => (
                <div key={i} className='flex items-center gap-2'>
                  <OccurrenceCodeCombobox
                    value={occ.code}
                    onChange={v => {
                      const next = [...occurrenceCodes];
                      next[i] = { ...next[i], code: v };
                      setOccurrenceCodes(next);
                    }}
                  />
                  <DatePicker
                    date={occ.date ? parseISOToLocalDate(occ.date) : undefined}
                    onDateChange={d => {
                      const next = [...occurrenceCodes];
                      next[i] = {
                        ...next[i],
                        date: d ? formatDateToISO(d) : '',
                      };
                      setOccurrenceCodes(next);
                    }}
                    placeholder='Select date'
                    datePickerSize='sm'
                    className='flex-1'
                  />
                  <button
                    onClick={() =>
                      setOccurrenceCodes(
                        occurrenceCodes.filter((_, j) => j !== i)
                      )
                    }
                    className='text-muted-foreground hover:text-destructive shrink-0'
                  >
                    <X className='h-4 w-4' />
                  </button>
                </div>
              ))}
              <button
                onClick={() =>
                  setOccurrenceCodes([
                    ...occurrenceCodes,
                    { code: '', date: '' },
                  ])
                }
                className='flex items-center gap-1 text-xs text-primary hover:underline'
              >
                <Plus className='h-3 w-3' /> Add Occurrence Code
              </button>
            </div>

            {/* Physician NPI */}
            <div className='space-y-1'>
              <label className='text-sm font-medium'>Physician NPI</label>
              <Input
                value={physicianNpi}
                onChange={e => setPhysicianNpi(e.target.value)}
                placeholder='10-digit NPI'
                maxLength={10}
                inputSize='sm'
              />
            </div>

            {/* Remarks */}
            <div className='space-y-1'>
              <label className='text-sm font-medium'>Remarks</label>
              <Textarea
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
                placeholder='Max 80 characters'
                maxLength={80}
                textareaSize='sm'
              />
              <p className='text-xs text-muted-foreground text-right'>
                {remarks.length}/80
              </p>
            </div>

            {/* DCN / ICN Number */}
            <div className='space-y-1'>
              <label className='text-sm font-medium'>DCN / ICN Number</label>
              <Input
                value={dcnIcnNumber}
                onChange={e => setDcnIcnNumber(e.target.value)}
                inputSize='sm'
              />
            </div>

            {/* Claim Number */}
            <div className='space-y-1'>
              <label className='text-sm font-medium'>Claim Number</label>
              <Input
                value={claimNumber}
                onChange={e => setClaimNumber(e.target.value)}
                inputSize='sm'
              />
            </div>

            {/* Sent Date */}
            <DateField
              label='Sent Date'
              htmlFor='noa_sent_date'
              value={sentDate || null}
              onChange={v => setSentDate(v ?? '')}
              fieldSize='sm'
            />
          </div>

          {/* KX Modifier — only shown when NOA is late */}
          {noa.is_late && (
            <div className='rounded-md border border-orange-200 bg-orange-50 p-3 space-y-2'>
              <p className='text-sm font-medium text-orange-800 flex items-center gap-1'>
                <AlertTriangle className='h-4 w-4' />
                This NOA is being submitted late ({Math.abs(noa.days_left)} days
                overdue).
              </p>
              <p className='text-sm text-orange-700'>
                Do you want to add a KX modifier on the final claim?
              </p>
              <div className='flex gap-4'>
                <label className='flex items-center gap-2 text-sm cursor-pointer'>
                  <input
                    type='radio'
                    name='kx_modifier'
                    checked={kxModifier === true}
                    onChange={() => setKxModifier(true)}
                  />
                  Yes, add to claim
                </label>
                <label className='flex items-center gap-2 text-sm cursor-pointer'>
                  <input
                    type='radio'
                    name='kx_modifier'
                    checked={kxModifier === false}
                    onChange={() => setKxModifier(false)}
                  />
                  No, do not add
                </label>
              </div>
              {kxModifier === true && (
                <div className='space-y-1'>
                  <label className='text-sm font-medium text-orange-800'>
                    Add remarks for final claim
                  </label>
                  <Textarea
                    value={kxModifierRemarks}
                    onChange={e => setKxModifierRemarks(e.target.value)}
                    placeholder='Remarks for final claim…'
                    maxLength={80}
                    textareaSize='sm'
                  />
                  <p className='text-xs text-orange-700 text-right'>
                    {kxModifierRemarks.length}/80
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className='mt-3 rounded-md bg-red-50 border border-red-200 p-2'>
            <p className='text-sm text-red-700'>{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className='mt-4 flex items-center gap-2 border-t pt-4'>
          <Button onClick={handleSave} disabled={isSaving} size='sm'>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
          <Button onClick={onClose} size='sm' variant='outline'>
            Cancel
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size='sm' variant='outline' disabled={isSaving}>
                Actions
                <ChevronDown className='h-3 w-3 ml-1' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start'>
              <DropdownMenuItem
                onClick={handleSaveAndSend}
                disabled={isSaving || localStatus !== 'ready_to_send'}
              >
                Submit to Medicare
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleForceMarkSent}
                disabled={isSaving || localStatus !== 'ready_to_send'}
              >
                Force Mark Sent
              </DropdownMenuItem>
              {canExportUb04 && <DropdownMenuSeparator />}
              {canExportUb04 && (
                <DropdownMenuItem onClick={() => handleExportPdf()}>
                  <Download className='h-3 w-3 mr-2' />
                  Export UB-04 PDF
                </DropdownMenuItem>
              )}
              {canExport837i && (
                <>
                  <DropdownMenuItem onClick={() => handleExport('edi')}>
                    <Download className='h-3 w-3 mr-2' />
                    Export EDI
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => handleExport('json')}>
                      <Download className='h-3 w-3 mr-2' />
                      Export JSON
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {localStatus !== 'not_applicable' ? (
            <Button
              onClick={handleMarkNotApplicable}
              disabled={isSaving}
              size='sm'
              variant='outline'
              className='ml-auto text-muted-foreground'
            >
              Mark Not Applicable
            </Button>
          ) : (
            <Button
              onClick={handleRevertToNotReady}
              disabled={isSaving}
              size='sm'
              variant='outline'
              className='ml-auto'
            >
              Revert to Not Yet Ready
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NOAForm;
